import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import {
  validatePhoneNumber,
  validateTIN,
  validateNameText,
  validateMerchantCategoryCode,
  formatAmount,
  convertCheckboxValue
} from "../../constants/validationFunctions";
import { v4 as uuidv4 } from "uuid";
import { countryToIso } from "../../sanitize/foreignAddress";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import {
  buildTransmitterGroup,
  buildVendorGroup,
} from "../../xml/transmitterData";

// Define the interface for the 1099-K records
export interface Form1099K {
  id: number;
  FILER_FEDERAL_ID_NUMBER: string;
  FILER_NAME: string;
  FILER_ADDRESS1?: string;
  FILER_ADDRESS2?: string;
  FILER_CITY?: string;
  FILER_STATE?: string;
  FILER_ZIP?: string;
  FILER_PHONE?: string;

  PAYEE_TIN: string;
  PAYEE_NAME: string;
  PAYEE_NAME2?: string;
  // TY2026 layout-V2 identity — the ETL maps legacy BOX_PAYEE_* columns to these
  // generic names; buildSplitRecipientDetail reads them.
  RECIPIENT_ID_TYPE?: string;
  RECIPIENT_FIRST_NAME?: string;
  RECIPIENT_MIDDLE_NAME?: string;
  RECIPIENT_LAST_NAME?: string;
  RECIPIENT_SUFFIX?: string;
  RECIPIENT_BUSINESS_NAME1?: string;
  RECIPIENT_BUSINESS_NAME2?: string;
  PAYEE_ADDRESS1?: string;
  PAYEE_ADDRESS2?: string;
  PAYEE_ADDRESS3?: string;
  PAYEE_CITY?: string;
  PAYEE_STATE?: string;
  PAYEE_ZIP?: string;

  TYPE_OF_PAYMENT_INDICATOR_PAYMENT_CARD?: string;
  TYPE_OF_PAYMENT_INDICATOR_THIRD_PARTY?: string;
  TYPE_OF_FILER_INDICATOR_PSE?: string;
  TYPE_OF_FILER_INDICATOR_3RD_PARTY?: string;
  PSE_NAME?: string;
  PSE_TELEPHONE?: string;
  PAYEE_INTERNAL_ACCT_NO?: string;

  BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS?: string;
  BOX1B_CARD_NOT_PRESENT_TRANS?: string;
  // TY2026 (schema v5.0.6) tips additions
  BOX1C_CASH_TIPS?: string;
  BOX1D_TTOC1?: string;
  BOX1D_TTOC2?: string;
  BOX2_MERCHANT_CAT_CODE?: string;
  BOX3_NO_PAYMENT_TRANSACTIONS?: string;
  BOX4_FED_INCOME_TAX_WTHLD?: string;

  BOX5A_JANUARY?: string;
  BOX5B_FEBRUARY?: string;
  BOX5C_MARCH?: string;
  BOX5D_APRIL?: string;
  BOX5E_MAY?: string;
  BOX5F_JUNE?: string;
  BOX5G_JULY?: string;
  BOX5H_AUGUST?: string;
  BOX5I_SEPTEMBER?: string;
  BOX5J_OCTOBER?: string;
  BOX5K_NOVEMBER?: string;
  BOX5L_DECEMBER?: string;

  BOX6A_STATE?: string;
  BOX6B_STATE?: string;
  BOX7A_STATE_ID_NO?: string;
  BOX7B_STATE_ID_NO?: string;
  BOX8A_STATE_TAX?: string;
  BOX8B_STATE_TAX?: string;

  FOREIGN_COUNTRY?: string;
  FOREIGN_COUNTRY_INDICATOR?: string;
  "2nd_TIN_Indicator"?: string;
  "1099_YEAR"?: string;
  corrected?: boolean;
  eFiled?: boolean;
}

export async function generate1099KXml(
  ctx: EngineContext,records: Form1099K[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  // Initialize the XML builder
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Group records by FILER_FEDERAL_ID_NUMBER
  const recordsByFiler: Record<string, Form1099K[]> = {};

  records.forEach((record) => {
    if (!record.FILER_FEDERAL_ID_NUMBER) {
      console.warn("Record missing FILER_FEDERAL_ID_NUMBER, using default");
      record.FILER_FEDERAL_ID_NUMBER = "000000000";
    }

    const filerId = record.FILER_FEDERAL_ID_NUMBER;
    if (!recordsByFiler[filerId]) {
      recordsByFiler[filerId] = [];
    }
    recordsByFiler[filerId].push(record);
  });

  // Generate unique transmission ID
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);

  // Determine tax year from the records or use current year as default
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  // Create a submission group for each filer
  const irSubmissionGroups = Object.keys(recordsByFiler).map((filerId) => {
    const filerRecords = recordsByFiler[filerId];
    const firstRecord = filerRecords[0]; // Use first record for filer info

    // Create submission ID for this filer
    const submissionId = generateUniqueSubmissionId();

    // Build the IssuerDetail from the record's filer information
    const issuerDetail: any = {
      ForeignEntityInd:
        firstRecord.FOREIGN_COUNTRY_INDICATOR === "X" ? "1" : "0",
      TIN: ctx.irs.testIndicator
        ? "000000000"
        : validateTIN(firstRecord.FILER_FEDERAL_ID_NUMBER),
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      PersonNameControlTxt: firstRecord.FILER_NAME
        ? firstRecord.FILER_NAME.trim().substring(0, 4).toUpperCase()
        : "PAYS",
    };

    // Add PersonName if available
    if (firstRecord.FILER_NAME) {
      issuerDetail["PersonName"] = {
        PersonFirstNm: validateNameText(firstRecord.FILER_NAME),
        PersonLastNm: validateNameText(firstRecord.FILER_NAME),
      };
    }

    // Add MailingAddressGrp if address info is available
    if (
      firstRecord.FILER_ADDRESS1 ||
      firstRecord.FILER_CITY ||
      firstRecord.FILER_STATE ||
      firstRecord.FILER_ZIP
    ) {
      // Determine if this is a foreign address
      const isForeign =
        firstRecord.FOREIGN_COUNTRY_INDICATOR === "X" &&
        firstRecord.FOREIGN_COUNTRY;

      if (isForeign && firstRecord.FOREIGN_COUNTRY) {
        issuerDetail["MailingAddressGrp"] = {
          ForeignAddress: {
            AddressLine1Txt: validateNameText(firstRecord.FILER_ADDRESS1),
            CityNm: validateNameText(firstRecord.FILER_CITY),
            CountryCd: countryToIso(firstRecord.FOREIGN_COUNTRY) || firstRecord.FOREIGN_COUNTRY,
          },
        };
      } else {
        issuerDetail["MailingAddressGrp"] = {
          USAddress: {},
        };

        if (firstRecord.FILER_ADDRESS1) {
          issuerDetail.MailingAddressGrp.USAddress["AddressLine1Txt"] =
            validateNameText(firstRecord.FILER_ADDRESS1);
        }

        if (firstRecord.FILER_ADDRESS2) {
          issuerDetail.MailingAddressGrp.USAddress["AddressLine2Txt"] =
            validateNameText(firstRecord.FILER_ADDRESS2);
        }

        if (firstRecord.FILER_CITY) {
          issuerDetail.MailingAddressGrp.USAddress["CityNm"] = validateNameText(
            firstRecord.FILER_CITY
          );
        }

        if (firstRecord.FILER_STATE) {
          issuerDetail.MailingAddressGrp.USAddress["StateAbbreviationCd"] =
            firstRecord.FILER_STATE;
        }

        if (firstRecord.FILER_ZIP) {
          issuerDetail.MailingAddressGrp.USAddress["ZIPCd"] =
            firstRecord.FILER_ZIP.replace(/\D/g, "").substring(0, 10);
        }
      }
    }

    // Add phone if available
    if (firstRecord.FILER_PHONE) {
      issuerDetail["PhoneNum"] = validatePhoneNumber(firstRecord.FILER_PHONE);
    }

    // Process recipient records for this filer
    const form1099KDetails = filerRecords.map((record) => {
      // Ensure exactly one of PSE or EPF is set to 1
      let isPSE = convertCheckboxValue(record.TYPE_OF_FILER_INDICATOR_PSE);
      let isEPF = convertCheckboxValue(record.TYPE_OF_FILER_INDICATOR_3RD_PARTY);
      
      // Apply Rule F1099K004: Sum of PSE and EPF must equal 1
      if (isPSE === isEPF) {
        // If both are 0 or both are 1, default to PSE=1, EPF=0
        isPSE = "1";
        isEPF = "0";
      }
      
      // Ensure exactly one of PaymentCard or ThirdPartyNetwork is set to 1
      let isPaymentCard = convertCheckboxValue(record.TYPE_OF_PAYMENT_INDICATOR_PAYMENT_CARD);
      let isThirdPartyNetwork = convertCheckboxValue(record.TYPE_OF_PAYMENT_INDICATOR_THIRD_PARTY);
      
      // Apply Rule F1099K005: Sum of PaymentCard and ThirdPartyNetwork must equal 1
      if (isPaymentCard === isThirdPartyNetwork) {
        // If both are 0 or both are 1, default to PaymentCard=1, ThirdPartyNetwork=0
        isPaymentCard = "1";
        isThirdPartyNetwork = "0";
      }

      // Build recipient record details
      const recipientDetail = {
        TIN: ctx.irs.testIndicator
          ? "000000000"
          : validateTIN(record.PAYEE_TIN),
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        PersonNameControlTxt: record.PAYEE_NAME
          ? record.PAYEE_NAME.trim().substring(0, 4).toUpperCase()
          : "UNKN",
        PersonName: {
          PersonFirstNm: validateNameText(record.PAYEE_NAME) || "Unknown",
          PersonLastNm:
            validateNameText(record.PAYEE_NAME2 || record.PAYEE_NAME) ||
            "Unknown",
        },
      };

      // Add mailing address based on whether it's foreign or domestic
      const isForeignRecipient =
        record.FOREIGN_COUNTRY_INDICATOR === "X" && record.FOREIGN_COUNTRY;

      if (isForeignRecipient && record.FOREIGN_COUNTRY) {
        recipientDetail["MailingAddressGrp"] = {
          ForeignAddress: {
            AddressLine1Txt:
              validateNameText(record.PAYEE_ADDRESS1) || "UNKNOWN ADDRESS",
            CityNm: validateNameText(record.PAYEE_CITY) || "UNKNOWN CITY",
            CountryCd: countryToIso(record.FOREIGN_COUNTRY) || record.FOREIGN_COUNTRY,
            ForeignPostalCd: record.PAYEE_ZIP,
          },
        };
      } else {
        recipientDetail["MailingAddressGrp"] = {
          USAddress: {
            AddressLine1Txt:
              validateNameText(record.PAYEE_ADDRESS1) || "UNKNOWN ADDRESS",
            CityNm: validateNameText(record.PAYEE_CITY) || "UNKNOWN CITY",
            StateAbbreviationCd: record.PAYEE_STATE || "XX",
            ZIPCd: record.PAYEE_ZIP?.replace(/\D/g, "") || "00000",
          },
        };
      }

      // Create the detail record object
      return {
        "@_xmlns": "urn:us:gov:treasury:irs:ir",
        "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
        TaxYr: taxYear.toString(),
        RecordId: (record.id).toString(),
        VoidInd: "0",
        CorrectedInd: record.corrected ? "1" : "0",
        // TY2026 layout-V2 records file via the shared split builder (business
        // as BusinessName+BUSINESS_TIN); legacy records keep the shape above.
        RecipientDetail:
          buildSplitRecipientDetail(
            record,
            ctx.irs.testIndicator
              ? "000000000"
              : validateTIN(record.PAYEE_TIN),
            recipientDetail["MailingAddressGrp"]
          ) ?? recipientDetail,

        ...(record.PAYEE_INTERNAL_ACCT_NO && {
          RecipientAccountNum: record.PAYEE_INTERNAL_ACCT_NO.substring(0, 30),
        }),

        // Use the corrected indicator values
        PSEInd: isPSE,
        EPFOrOtherThirdPartyInd: isEPF, 
        PaymentCardInd: isPaymentCard,
        ThirdPartyNetworkInd: isThirdPartyNetwork,

        ...(record.PSE_NAME && {
          PSENm: validateNameText(record.PSE_NAME),
        }),

        // PSEPhoneNum is OPTIONAL — emit it only when it's a valid 10-digit number; otherwise
        // omit it (validatePhoneNumber just strips non-digits, so a short source phone stays short
        // and would fail the minLength facet). A bad phone shouldn't block the whole filing.
        ...(/^\d{10}$/.test(validatePhoneNumber(record.PSE_TELEPHONE || "") || "")
          ? { PSEPhoneNum: validatePhoneNumber(record.PSE_TELEPHONE || "") }
          : {}),

        SecondTINNoticeInd: record["2nd_TIN_Indicator"] === "X" ? "1" : "0",

        ...(record.BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS && {
          GrossAmt: formatAmount(record.BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS),
        }),

        ...(record.BOX1B_CARD_NOT_PRESENT_TRANS && {
          CardNotPresentTransAmt: formatAmount(
            record.BOX1B_CARD_NOT_PRESENT_TRANS
          ),
        }),

        // TY2026 (schema v5.0.6) additions, minOccurs=0 — emit only when the
        // record carries a value AND the transmission's schema year supports
        // them (2025-clamped transmissions must not contain these elements).
        // XSD order: CardNotPresentTransAmt → CashTipsAmt →
        // TreasuryTippedOccupationCd → MerchantCategoryCd.
        ...(Number(taxYear) >= 2026 &&
          Number(record.BOX1C_CASH_TIPS) > 0 && {
            CashTipsAmt: formatAmount(record.BOX1C_CASH_TIPS),
          }),
        ...(Number(taxYear) >= 2026 &&
          (record.BOX1D_TTOC1 || record.BOX1D_TTOC2) && {
            // maxOccurs=2 — array emits one element per code
            TreasuryTippedOccupationCd: [record.BOX1D_TTOC1, record.BOX1D_TTOC2]
              .filter((c) => c && String(c).trim() !== "")
              .map((c) => String(c).trim()),
          }),

        ...(validateMerchantCategoryCode(record.BOX2_MERCHANT_CAT_CODE) && {
          MerchantCategoryCd: validateMerchantCategoryCode(
            record.BOX2_MERCHANT_CAT_CODE
          ),
        }),

        ...(record.BOX3_NO_PAYMENT_TRANSACTIONS && {
          PaymentTransactionCnt:
            parseInt(record.BOX3_NO_PAYMENT_TRANSACTIONS).toString() || "0",
        }),

        ...(record.BOX4_FED_INCOME_TAX_WTHLD && {
          FederalIncomeTaxWithheldAmt: formatAmount(
            record.BOX4_FED_INCOME_TAX_WTHLD
          ),
        }),

        ...(record.BOX5A_JANUARY && {
          JanuaryAmt: formatAmount(record.BOX5A_JANUARY),
        }),

        ...(record.BOX5B_FEBRUARY && {
          FebruaryAmt: formatAmount(record.BOX5B_FEBRUARY),
        }),

        ...(record.BOX5C_MARCH && {
          MarchAmt: formatAmount(record.BOX5C_MARCH),
        }),

        ...(record.BOX5D_APRIL && {
          AprilAmt: formatAmount(record.BOX5D_APRIL),
        }),

        ...(record.BOX5E_MAY && {
          MayAmt: formatAmount(record.BOX5E_MAY),
        }),

        ...(record.BOX5F_JUNE && {
          JuneAmt: formatAmount(record.BOX5F_JUNE),
        }),

        ...(record.BOX5G_JULY && {
          JulyAmt: formatAmount(record.BOX5G_JULY),
        }),

        ...(record.BOX5H_AUGUST && {
          AugustAmt: formatAmount(record.BOX5H_AUGUST),
        }),

        ...(record.BOX5I_SEPTEMBER && {
          SeptemberAmt: formatAmount(record.BOX5I_SEPTEMBER),
        }),

        ...(record.BOX5J_OCTOBER && {
          OctoberAmt: formatAmount(record.BOX5J_OCTOBER),
        }),

        ...(record.BOX5K_NOVEMBER && {
          NovemberAmt: formatAmount(record.BOX5K_NOVEMBER),
        }),

        ...(record.BOX5L_DECEMBER && {
          DecemberAmt: formatAmount(record.BOX5L_DECEMBER),
        }),

        ...(shouldIncludeStateLocalTaxGrp(record) && {
          StateLocalTaxGrp: getStateLocalTaxGrp(record),
        }),
      };
    });

    // Calculate totals for this filer's records
    const totals = calculateTotalsForFiler(filerRecords, taxYear);

    // Return a submission group for this filer
    return {
      IRSubmission1Header: {
        SubmissionId: submissionId,
        TaxYr: taxYear.toString(),
        IssuerDetail: issuerDetail,
        ContactPersonInformationGrp: {
          ContactPersonNm:
            validateNameText(firstRecord.FILER_NAME) || "Contact Person",
          ContactPhoneNum:
            validatePhoneNumber(firstRecord.FILER_PHONE) || "0000000000",
          ContactEmailAddressTxt: "support@ts1099.com",
        },
        FormTypeCd: "1099K",
        ParentFormTypeCd: "1096",
        CFSFElectionInd: "0",
        JuratSignatureGrp: {
          SignatureIntentInd: "1",
          JuratSignaturePIN: "00000",
          SignatureDt: new Date().toISOString().split("T")[0],
          JuratPersonTitleTxt: "Payer",
          PersonNm: validateNameText(firstRecord.FILER_NAME) || "Payer",
        },
        TotalReportedRcpntFormCnt: form1099KDetails.length.toString(),
        IRSubmission1FormTotals: {
          Form1099KTotalAmtGrp: totals,
        },
      },
      IRSubmission1Detail: {
        Form1099KDetail: form1099KDetails,
      },
    };
  });

  // Build the complete envelope structure
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear.toString(),
        PriorYearDataInd: "0",
        TransmissionTypeCd: "O",
        TestCd: ctx.irs.testIndicator ? "T" : "P",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: Object.keys(recordsByFiler).length.toString(),
        TotalRecipientFormCnt: records.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: irSubmissionGroups,
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear, uniqueTransmissionId };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}

// Helper function to calculate totals for a specific filer's records
function calculateTotalsForFiler(
  records: Form1099K[],
  taxYear: string | number
): any {
  // Initialize totals object.
  // TY2026 CashTipsAmt sits between CardNotPresentTransAmt and JanuaryAmt in
  // Form1099KTotalAmtGrpType — key order here IS the XML element order.
  const totals: any = {
    FederalIncomeTaxWithheldAmt: "0",
    GrossAmt: "0",
    CardNotPresentTransAmt: "0",
    ...(Number(taxYear) >= 2026 &&
      records.some((r) => Number(r.BOX1C_CASH_TIPS) > 0) && {
        CashTipsAmt: records
          .reduce(
            (sum, r) => sum + (parseFloat(r.BOX1C_CASH_TIPS || "0") || 0),
            0
          )
          .toFixed(2),
      }),
    JanuaryAmt: "0",
    FebruaryAmt: "0",
    MarchAmt: "0",
    AprilAmt: "0",
    MayAmt: "0",
    JuneAmt: "0",
    JulyAmt: "0",
    AugustAmt: "0",
    SeptemberAmt: "0",
    OctoberAmt: "0",
    NovemberAmt: "0",
    DecemberAmt: "0",
  };

  // Calculate actual totals from records
  records.forEach((record) => {
    if (record.BOX4_FED_INCOME_TAX_WTHLD) {
      const amount = parseInt(formatAmount(record.BOX4_FED_INCOME_TAX_WTHLD));
      totals.FederalIncomeTaxWithheldAmt = (
        parseInt(totals.FederalIncomeTaxWithheldAmt) + amount
      ).toString();
    }

    if (record.BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS) {
      const amount = parseInt(
        formatAmount(record.BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS)
      );
      totals.GrossAmt = (parseInt(totals.GrossAmt) + amount).toString();
    }

    if (record.BOX1B_CARD_NOT_PRESENT_TRANS) {
      const amount = parseInt(
        formatAmount(record.BOX1B_CARD_NOT_PRESENT_TRANS)
      );
      totals.CardNotPresentTransAmt = (
        parseInt(totals.CardNotPresentTransAmt) + amount
      ).toString();
    }

    // Monthly amounts
    if (record.BOX5A_JANUARY) {
      const amount = parseInt(formatAmount(record.BOX5A_JANUARY));
      totals.JanuaryAmt = (parseInt(totals.JanuaryAmt) + amount).toString();
    }

    if (record.BOX5B_FEBRUARY) {
      const amount = parseInt(formatAmount(record.BOX5B_FEBRUARY));
      totals.FebruaryAmt = (parseInt(totals.FebruaryAmt) + amount).toString();
    }

    if (record.BOX5C_MARCH) {
      const amount = parseInt(formatAmount(record.BOX5C_MARCH));
      totals.MarchAmt = (parseInt(totals.MarchAmt) + amount).toString();
    }

    if (record.BOX5D_APRIL) {
      const amount = parseInt(formatAmount(record.BOX5D_APRIL));
      totals.AprilAmt = (parseInt(totals.AprilAmt) + amount).toString();
    }

    if (record.BOX5E_MAY) {
      const amount = parseInt(formatAmount(record.BOX5E_MAY));
      totals.MayAmt = (parseInt(totals.MayAmt) + amount).toString();
    }

    if (record.BOX5F_JUNE) {
      const amount = parseInt(formatAmount(record.BOX5F_JUNE));
      totals.JuneAmt = (parseInt(totals.JuneAmt) + amount).toString();
    }

    if (record.BOX5G_JULY) {
      const amount = parseInt(formatAmount(record.BOX5G_JULY));
      totals.JulyAmt = (parseInt(totals.JulyAmt) + amount).toString();
    }

    if (record.BOX5H_AUGUST) {
      const amount = parseInt(formatAmount(record.BOX5H_AUGUST));
      totals.AugustAmt = (parseInt(totals.AugustAmt) + amount).toString();
    }

    if (record.BOX5I_SEPTEMBER) {
      const amount = parseInt(formatAmount(record.BOX5I_SEPTEMBER));
      totals.SeptemberAmt = (parseInt(totals.SeptemberAmt) + amount).toString();
    }

    if (record.BOX5J_OCTOBER) {
      const amount = parseInt(formatAmount(record.BOX5J_OCTOBER));
      totals.OctoberAmt = (parseInt(totals.OctoberAmt) + amount).toString();
    }

    if (record.BOX5K_NOVEMBER) {
      const amount = parseInt(formatAmount(record.BOX5K_NOVEMBER));
      totals.NovemberAmt = (parseInt(totals.NovemberAmt) + amount).toString();
    }

    if (record.BOX5L_DECEMBER) {
      const amount = parseInt(formatAmount(record.BOX5L_DECEMBER));
      totals.DecemberAmt = (parseInt(totals.DecemberAmt) + amount).toString();
    }
  });

  // Remove TotalReportedAmt from totals as it's not expected in the schema
  return totals;
}

/**
 * Helper function to determine if state/local tax groups should be included.
 */
function shouldIncludeStateLocalTaxGrp(record: Form1099K): boolean {
  return !!(
    (record.BOX6A_STATE &&
      record.BOX7A_STATE_ID_NO &&
      record.BOX8A_STATE_TAX) ||
    (record.BOX6B_STATE && record.BOX7B_STATE_ID_NO && record.BOX8B_STATE_TAX)
  );
}

/**
 * Helper function to build state/local tax group arrays.
 */
function getStateLocalTaxGrp(record: Form1099K): any[] {
  const groups = [];

  if (record.BOX6A_STATE && record.BOX7A_STATE_ID_NO) {
    groups.push({
      StateAbbreviationCd: record.BOX6A_STATE.substring(0, 2),
      StateTaxGrp: {
        StateIdNum: record.BOX7A_STATE_ID_NO.substring(0, 20),
        StateTaxWithheldAmt: formatAmount(record.BOX8A_STATE_TAX),
        StateIncomeAmt: formatAmount(
          record.BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS || "0"
        ),
      },
    });
  }

  if (record.BOX6B_STATE && record.BOX7B_STATE_ID_NO) {
    groups.push({
      StateAbbreviationCd: record.BOX6B_STATE.substring(0, 2),
      StateTaxGrp: {
        StateIdNum: record.BOX7B_STATE_ID_NO.substring(0, 20),
        StateTaxWithheldAmt: formatAmount(record.BOX8B_STATE_TAX),
        StateIncomeAmt: formatAmount(
          record.BOX1A_GROSS_AMT_PAYMENT_CARD_TPN_TRANS || "0"
        ),
      },
    });
  }

  return groups;
}
